import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AdminContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchAdmin();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchAdmin = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setAdmin(response.data);
    } catch (error) {
      console.error('Failed to fetch admin:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, admin_id, role, username } = response.data;
    localStorage.setItem('admin_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setAdmin({ id: admin_id, role, username, email });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setAdmin(null);
  };

  const hasPermission = (permission) => {
    if (!admin) return false;
    const permissions = {
      super_admin: ["*"],
      admin: ["users:read", "users:write", "users:ban", "users:suspend", "reports:read", "reports:write", "reports:resolve", "analytics:read", "broadcasts:write", "audit:read"],
      moderator: ["users:read", "users:warn", "reports:read", "reports:write", "reports:resolve"],
      support: ["users:read", "reports:read"]
    };
    const rolePerms = permissions[admin.role] || [];
    return rolePerms.includes("*") || rolePerms.includes(permission);
  };

  return (
    <AdminContext.Provider value={{
      admin,
      token,
      loading,
      login,
      logout,
      hasPermission,
      isAuthenticated: !!token && !!admin
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
